/* eslint-disable */
import axios from '@fs/zion-axios'
import { getNameInPreferredScript } from '@fs/zion-person-service'
import { i18n } from '@fs/zion-locale'

export const checkIsChinese = (ch) => {
  const REGEX_CHINESE = /[\u4e00-\u9fff]|[\u3400-\u4dbf]|[\u{20000}-\u{2a6df}]|[\u{2a700}-\u{2b73f}]|[\u{2b740}-\u{2b81f}]|[\u{2b820}-\u{2ceaf}]|[\uf900-\ufaff]|[\u3300-\u33ff]|[\ufe30-\ufe4f]|[\uf900-\ufaff]|[\u{2f800}-\u{2fa1f}]/u
  return REGEX_CHINESE.test(ch)
}
export const checkIsJapanese = (ch) => {
  const REGEX_JAPANESE = /[\u3000-\u303f]|[\u3040-\u309f]|[\u30a0-\u30ff]|[\uff00-\uff9f]|[\u4e00-\u9faf]|[\u3400-\u4dbf]/
  return REGEX_JAPANESE.test(ch)
}
export const checkIsKorean = (ch) => {
  const REGEX_KOREAN = /[\uac00-\ud7af]|[\u1100-\u11ff]|[\u3130-\u318f]|[\ua960-\ua97f]|[\ud7b0-\ud7ff]/
  return REGEX_KOREAN.test(ch)
}

export const checkAsianChar = (ch) => {
  // check if an Asian character was entered
  return checkIsChinese(ch) || checkIsJapanese(ch) || checkIsKorean(ch)
}

const breakNameIntoGroups = (name) => {
  // Library of name parts
  const twoPartNames = ['La', 'Le', 'Mac', 'Mc', 'San', 'Santa', 'Santo', 'Saint', 'Sainte', 'st', 'st.', 'te', 'van', 'van der', 'Von',]
  const prefixes = ['Dr', 'Dr.', 'Professor', 'Mr.', 'Mrs.', 'Ms.', 'Miss', 'Sir', 'Chancelor', 'Principal', 'President', 'Master', 'Lady', 'Lord', 'Warden']
  const suffixes = ['I', 'II', 'III', 'IV', 'Sr.', 'Sr', 'sr', 'Jr.', 'Jr', 'jr', 'vda.', 'vda', 'geb', 'geb.', 'MD',]
  const connectors = ['da', 'das', 'dei', 'Del', 'del', 'della', 'delle', 'la', 'las', 'los', 'De', 'de', 'Di', 'di', 'Do', 'do', 'dos', '&', 'and', 'e', 'y', 'or',]

  // Identify what type the part of the name is
  const identifyPartType = (part) => {
    if (part.match(/\(/)) return 'alternative'
    else if (twoPartNames.includes(part)) return 'twoPartName'
    else if (prefixes.includes(part)) return 'prefix'
    else if (suffixes.includes(part)) return 'suffix'
    else if (connectors.includes(part)) return 'connector'
    else return 'name'
  }

  // Loop through the name, putting name parts into groups
  const parts = name.split(' ')
  const groups = []
  let group = {}
  let lookahead = {}
  for (let part of parts) {
    part = part.replace(',', '').trim()
    let partType = identifyPartType(part)
    switch (partType) {
      case 'alternative':
        lookahead.alternative = part.replace('(', '').replace(')', '')
        break
      case 'twoPartName':
        if (!group.name) group.name = ''
        group.name += `${part} `
        break
      case 'prefix':
        if (!group.prefixes) group.prefixes = []
        group.prefixes.push(part)
        break
      case 'suffix':
        if (!lookahead.suffixes) lookahead.suffixes = []
        lookahead.suffixes.push(part)
        break
      case 'connector':
        if (!group.connectors) group.connectors = []
        group.connectors.push(part)
        break
      default:
        if (!group.name) group.name = ''
        group.name += part
        lookahead = group
        groups.push(group)
        group = {}
        break
    }
  }
  const filteredGroups = groups.filter(nameObject => nameObject.name !== "")
  return filteredGroups
}

export const deconstructName = (displayName, name, surname, isAsian, showRoman) => {
  const response = {}
  if (isAsian) {
    // No breakNameIntoGroups here because no way to separate parts of CJK names, suffixes, or titles.
    const nameParts = displayName?.split(' ')
    response.middleNames = [] // CJK names have no 'middleNames'
    response.firstName = { name: nameParts?.length > 1 && showRoman ? nameParts[1] : name } // IF we have romanized CJK form use those parts, ELSE use original
    response.lastName = { name: nameParts?.length > 1 && showRoman ? nameParts[0] : surname }
    return response
  }
  // get the name groups
  const groups = breakNameIntoGroups(displayName)
  // construct the output based on the groups
  if (groups.length) response.firstName = groups[0]
  if (groups.length > 1) response.lastName = groups[groups.length - 1]
  if (groups.length > 2) response.middleNames = groups.slice(1, groups.length - 1)
  return response
  // Example name: "Dr. San Pedro (Peter) Paredes Espinosa de los Monteros III, MD"
  // deconstructName returns:
  // {
  //     firstName: nameObject,
  //     middleNames: [nameObject],
  //     lastname: nameObject
  // }
  //
  // nameObject:
  // {
  //     name: "San Pedro",
  //     alternative: "Peter",
  //     prefixes: ["Dr."],
  //     connectors: ["de los"],
  //     suffixes: ["III","MD"]
  // }
}

export const getNameFormatByLocale = (locale) => {
  return axios.get(`/service/standards/core/ws/core/locales/${locale}`)
}


/**
* @param name - givenName, including firstName + middleNames as one string
* @param surname - surname, which comes first if name is CJK
*/
export const orderName = ({name, surname}) => {
  if (!name && !surname) return i18n.t("unknown")
  name = name && typeof name === 'string' ? name : '' // make sure names are strings, not bools or numeric
  surname = surname && typeof surname === 'string' ? surname : ''
  const firstName = name?.split(' ')[0] // remove middle names from givenName
  if (firstName === '' && surname.trim() === '') return i18n.t("unknown")
  const isAsian = !!(checkAsianChar(firstName) || checkAsianChar(surname))
  return isAsian ? `${surname}${firstName}`.trim() : `${firstName} ${surname}`.trim()
}


/**
  `${surname}${name}` - IF default name+surname is CJK AND showNamesInRomanScript setting is off

  `${surname} ${name}` - IF default name+surname is CJK AND other nameForms exist AND showNamesInRomanScript setting is on

  `${name} ${surname}` - IF default name+surname is NOT CJK
  @param treePerson - { name, surname, names, ...rest }
  @param showRoman - showNamesInRomanScript user preference tells whether to display romanized CJK names or not
*/
export const getDisplayName = (treePerson, showRoman) => {
  if (!treePerson) return i18n.t('unknown')
  if(treePerson?.name && typeof(treePerson.name)==='string' && !treePerson?.names && !treePerson?.surname) return treePerson?.name

  const { name, surname } = treePerson
  const [{ nameForms }] = treePerson?.names?.filter((nameOption) => !!nameOption?.preferred) ?? [{nameForms: []}] // get nameForms of preferred name

  /* IF nameForms has any romanized forms & the preference is on, use that romanized form, ELSE return what we already have ordered correctly */
  if (nameForms?.length > 1 && showRoman) {
    let nameFormName
    treePerson['nameConclusion'] = { details: { nameForms: nameForms } } // getNameInPreferredScript expects these properties on person
    try {
      /* FYI: getNameInPreferredScript thinks personObj.name is the full name, ie: DON'T rely on it to order our names, just to return the romanized form */
      const { fullName } = getNameInPreferredScript(treePerson, showRoman)
      nameFormName = fullName
    }
    catch(e){
      nameFormName= i18n.t("unknown")
    }
    return nameFormName
  }

  return orderName({name, surname})
}
